import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { DefaultFiltersType } from "../../newDocuments/types";

type DocumentStatus = "NEW" | "READ" | "UNREAD";

export interface Document {
  category: string;
  content: any;
  createdOn: string;
  customTags: any[];
  documentId?: string;
  documentSearchScorePercentage: string;
  einvoice: boolean;
  encrypted: boolean;
  favourite: boolean;
  fileName: string;
  infoList: { [key: string]: any }[];
  notesCount: number;
  status: DocumentStatus;
  standardTags: { [key: string]: any }[];
  subTitle: string;
  title: string;
  updatedOn: string;
  urgent: boolean;
  userType: string;
  visibleByAccountantOnly: boolean;
}

interface DocumentListState {
  documentDownloaded: boolean;
  documentFilterOption: string;
  documentList: Document[] | null;
  documentsSelected: Document[];
  filterByNewestDate: boolean;
  filterSettings: DefaultFiltersType;
  hasMoreDocuments: boolean;
  indeterminateCheck: boolean;
  isFiltering: boolean;
  loading: boolean;
  onFilterChange: boolean;
  onScroll: any;
  pageNumber: number;
  selectAllDocuments: boolean;
  addDocument: (document: Document) => void;
  clearSelectedDocuments: () => void;
  handleDocumentUpdate: (updatedDocumentList: string[]) => void;
  incrementPageNumber: () => void;
  removeSelectedDocument: (document: Document) => void;
  setAllDocuments: (documents: Document[]) => void;
  setDocumentFilterOption: (documentFilterOption: string) => void;
  setDocumentList: (documentList: any) => void;
  setDocumentStatus: (documentId: string, status: string) => void;
  setDocumentUrgent: (documentId: string, newUrgent: boolean) => void;
  setHasMoreDocuments: (hasMore: boolean) => void;
  setIndeterminateCheck: () => void;
  setIsFiltering: (isFiltering: boolean) => void;
  setLoading: (value: boolean) => void;
  setOnFilterChange: (value: boolean) => void;
  setOnScroll: (value: any) => void;
  setFilterSetting: (key: keyof DefaultFiltersType, value: any) => void;
  toggleDocumentDownload: () => void;
  toggleDocumentSelection: (document: Document) => void;
  toggleFilterByDate: () => void;
  toggleSelectAllDocuments: (isChecked: boolean) => void;
  toggleLoading: () => void;
}

export const useDocumentList = create<DocumentListState>()(
  devtools(
    (set, get) => ({
      documentDownloaded: false,
      documentFilterOption: "",
      documentList: [],
      documentsSelected: [],
      filterByNewestDate: false,
      filterSettings: {
        aggregationsFilter: {},
        dateFilters: [{ startDate: "", endDate: "" }],
        dateType: "UPLOAD",
        filterPerYear: "",
        fullTextSearch: "",
        invoiceNumber: "",
        isFavourite: false,
        isUrgent: false,
        microCategory: [],
        onlyVisibleToMe: false,
        pageNumber: 0,
        pageSize: 20,
        readByAccountant: true,
        readByCompany: false,
        selectedChip: "Inbox",
        selectedMicro: "",
        sort: "date",
        sortOption: "LATEST_UPLOADS",
        tags: [],
        urgentDocs: false,
      },
      hasMoreDocuments: true,
      indeterminateCheck: false,
      isFiltering: false,
      loading: false,
      onFilterChange: false,
      onScroll: false,
      pageNumber: 0,
      selectAllDocuments: false,

      addDocument: (document: Document) => {
        set((state) => ({
          documentsSelected: [...state.documentsSelected, document],
          indeterminateCheck: state.documentsSelected.length + 1 > 0,
        }));
      },

      clearSelectedDocuments: () => {
        set({
          documentsSelected: [],
          indeterminateCheck: false,
          selectAllDocuments: false,
        });
      },

      handleDocumentUpdate: (updatedDocumentList: string[]) => {
        const { documentsSelected, documentList } = get();
        const idsToRemove = updatedDocumentList;
        const filteredDocumentList =
          documentList?.filter(
            (doc) => !idsToRemove.includes(doc.documentId)
          ) || [];
        // Update the state with the filtered lists
        set({
          documentList: filteredDocumentList,
          documentsSelected: documentsSelected.filter(
            (doc) => !idsToRemove.includes(doc.documentId)
          ),
          selectAllDocuments: false,
          indeterminateCheck: false,
        });
      },

      incrementPageNumber: () =>
        set({ pageNumber: get().pageNumber + 1, onFilterChange: false }),

      removeSelectedDocument: (document: Document) => {
        set((state) => ({
          documentsSelected: state.documentsSelected.filter(
            (doc) => doc.documentId !== document.documentId
          ),
          indeterminateCheck: state.documentsSelected.length > 1,
        }));
      },

      setAllDocuments: (documents: Document[]) => {
        set({
          documentsSelected: documents,
          indeterminateCheck: documents.length > 0,
        });
      },

      setDocumentFilterOption: (documentFilterOption: string) => {
        set({
          documentFilterOption,
        });
      },

      setDocumentList: (documentList) => {
        set((state) => {
          const prevDocumentList = state.onFilterChange
            ? []
            : state.documentList;
          const combinedDocumentList = [...prevDocumentList, ...documentList];
          const uniqueDocumentList = combinedDocumentList.filter(
            (doc, index, self) =>
              index === self.findIndex((t) => t.documentId === doc.documentId)
          );

          return {
            documentList: uniqueDocumentList,
            documentsSelected: [],
            selectAllDocuments: false,
            indeterminateCheck: false,
          };
        });
      },

      setDocumentStatus: (documentId: string, newStatus: DocumentStatus) => {
        set((state) => ({
          documentList: state.documentList.map((doc) =>
            doc.documentId === documentId ? { ...doc, status: newStatus } : doc
          ),
        }));
      },

      setDocumentUrgent: (documentId: string, newUrgent: boolean) => {
        set((state) => ({
          documentList: state.documentList.map((doc) =>
            doc.documentId === documentId ? { ...doc, urgent: newUrgent } : doc
          ),
        }));
      },

      setHasMoreDocuments: (hasMore: boolean) => {
        set({ hasMoreDocuments: hasMore });
      },

      setIndeterminateCheck: () => {
        const { selectAllDocuments, documentsSelected } = get();
        set({
          indeterminateCheck:
            !selectAllDocuments && documentsSelected.length > 0,
        });
      },
      setIsFiltering: (isFiltering: boolean) => {
        set({ isFiltering: isFiltering });
      },

      setLoading: (value: boolean) => {
        set({ loading: value });
      },

      setOnFilterChange: (value) => {
        set(() => ({ onFilterChange: value, pageNumber: 0 }));
      },

      setOnScroll: (value) => {
        set(() => ({ onScroll: value }));
      },

      setFilterSetting: (key: keyof DefaultFiltersType, value: any) => {
        set((state) => ({
          filterSettings: {
            ...state.filterSettings,
            [key]: value,
          },
        }));
      },
      toggleDocumentDownload: () => {
        set({ documentDownloaded: true });
        setTimeout(() => set({ documentDownloaded: false }), 1000);
      },

      toggleDocumentSelection: (document: Document) => {
        const { documentsSelected, documentList } = get();
        const index = documentsSelected.findIndex(
          (doc) => doc.documentId === document.documentId
        );

        const updatedDocumentsSelected =
          index !== -1
            ? documentsSelected.filter(
                (doc) => doc.documentId !== document.documentId
              )
            : [...documentsSelected, document];

        set({
          documentsSelected: updatedDocumentsSelected,
          selectAllDocuments:
            updatedDocumentsSelected.length === (documentList?.length || 0),
          indeterminateCheck:
            updatedDocumentsSelected.length > 0 &&
            updatedDocumentsSelected.length < (documentList?.length || 0),
        });
      },

      toggleFilterByDate: () => {
        set((state) => ({ filterByNewestDate: !state.filterByNewestDate }));
      },

      toggleLoading: () => {
        set((state) => ({ loading: !state.loading }));
      },

      toggleSelectAllDocuments: (isChecked: boolean) => {
        const documentList = get().documentList || [];
        set({
          selectAllDocuments: isChecked,
          documentsSelected: isChecked ? [...documentList] : [],
          indeterminateCheck: false,
        });
      },
    }),
    { name: "DocumentListStore" }
  )
);
